import React from "react";
import { Link } from "react-router-dom";
import { Spinner, Alert, Button } from "react-bootstrap";
import { useRequest } from "../hooks/useRequest";
import { usePolledRequest } from "../hooks/usePolledRequest";
import { useGameClock } from "../hooks/useGameClock";
import { useAuth } from "../context/AuthContext";
import { useLeague } from "../hooks/useLeague";
import { useLeaguePublicContext } from "./LeaguePublicLayout";
import { getSeasonStatsUrl, getGamesUrl, getGameUrl } from "../config/api";
import { Game, TeamStat } from "../types/models";
import { PaginatedResponse } from "../types/api";
import TeamDisplay from "../Common/TeamDisplay";
import GameListItem from "../Common/GameListItem";
import { selectGamesPerTeam } from "../utils/gameSelection";
import {
  formatClock,
  periodShortLabel,
  regulationPeriodsOf,
} from "../utils/gameClock";
import LiveRefreshIndicator, { LIVE_REFRESH_MS } from "./LiveRefreshIndicator";

// --- Types ------------------------------------------------------------------

interface GameWithDetails extends Game {
  goals?: never;
}

function unwrapPaginated<T>(
  response: PaginatedResponse<T> | T[] | null | undefined,
): T[] {
  if (!response) return [];
  return Array.isArray(response) ? response : response.data;
}

// --- Components -------------------------------------------------------------

function LiveGameBanner({ game }: { game: GameWithDetails }) {
  const auth = useAuth();
  const { currentLeague } = useLeague();

  // The season's game list is a snapshot of page load, so the banner reads the
  // game itself on a timer: the score, the clock and the period all move while
  // someone is watching.
  const { data: polled, isRefreshing } = usePolledRequest<GameWithDetails>(
    getGameUrl(game.id),
    auth.token,
    LIVE_REFRESH_MS,
    game.state === "playing",
  );

  const live = polled ?? game;
  const clockSeconds = useGameClock(live);
  const homeLeading = (live.home_score ?? 0) >= (live.visitor_score ?? 0);

  // A game that has ended while the page was open is no longer live, so the
  // banner goes: the result belongs in the results list.
  if (live.state !== "playing") return null;

  return (
    <Link
      to={`games/${live.id}`}
      style={{ textDecoration: "none", color: "inherit", display: "block" }}
    >
      <div
        className="lp-live-banner"
        role="region"
        aria-label="Live game in progress"
        style={{ cursor: "pointer" }}
      >
        <div className="lp-live-banner-header">
          <span className="lp-live-dot" aria-hidden="true" />
          <span className="lp-live-label">Live now</span>
          <span className="lp-live-clock" aria-label="Game clock">
            {formatClock(clockSeconds)}
          </span>
          <span className="lp-live-period">
            {periodShortLabel(
              live.current_period,
              regulationPeriodsOf(currentLeague?.period_minutes),
            )}
          </span>
          <LiveRefreshIndicator isRefreshing={isRefreshing} />
        </div>
        <div className="lp-live-matchup">
          <div className="lp-live-teams">
            <div className={`lp-live-team${!homeLeading ? " leading" : ""}`}>
              <TeamDisplay
                team={{
                  id: live.visitor_team_id,
                  name: live.visitor_team_name ?? "Visitor Team",
                  colour_code: live.visitor_team_colour_code,
                }}
              />
            </div>
            <div className={`lp-live-team${homeLeading ? " leading" : ""}`}>
              <TeamDisplay
                team={{
                  id: live.home_team_id,
                  name: live.home_team_name ?? "Home Team",
                  colour_code: live.home_team_colour_code,
                }}
              />
            </div>
          </div>
          <div className="lp-live-scores" aria-label="Score">
            <span className={`lp-live-score${!homeLeading ? " leading" : ""}`}>
              {live.visitor_score ?? 0}
            </span>
            <span className={`lp-live-score${homeLeading ? " leading" : ""}`}>
              {live.home_score ?? 0}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// --- Main component ---------------------------------------------------------

// Both game lists cover every team in the league rather than a fixed number of
// games, so each team's next (and last) two games are always represented.
const GAMES_PER_TEAM = 2;

export default function HomePage() {
  const auth = useAuth();
  const { setSelectedSeasonId } = useLeague();
  const { seasonId, seasonOptions } = useLeaguePublicContext();

  // Team stats — regular season
  // Endpoint: GET /seasons/:id/team_stats?game_type=regular
  const statsUrl = seasonId
    ? getSeasonStatsUrl(seasonId, "regular")
    : undefined;
  const { data: statsResponse } = useRequest<TeamStat[]>(statsUrl, auth.token);

  // All games for the season
  // Endpoint: GET /seasons/:id/games
  const gamesUrl = seasonId ? getGamesUrl(seasonId) : undefined;
  const { data: gamesResponse } = useRequest<
    PaginatedResponse<GameWithDetails>
  >(gamesUrl, auth.token);

  if (!seasonId) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  const allGames = unwrapPaginated(gamesResponse);

  // Split games by state
  const liveGames = allGames.filter((g) => g.state === "playing");

  const completedGames = allGames
    .filter((g) => g.state === "completed")
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const recentGames = selectGamesPerTeam(completedGames, GAMES_PER_TEAM);

  const upcomingGames = selectGamesPerTeam(
    allGames
      .filter((g) => g.state === "scheduled")
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    GAMES_PER_TEAM,
  );

  // Sort standings
  const standings = [...(statsResponse ?? [])].sort((a, b) => {
    const ptsDiff = b.points - a.points;
    if (ptsDiff !== 0) return ptsDiff;
    return b.goals_for - b.goals_against - (a.goals_for - a.goals_against);
  });

  const isLoading = !statsResponse && !gamesResponse;

  // Check if viewing a non-latest season
  const currentSeasonOption = seasonOptions.find(
    (opt) => opt.seasonId === seasonId,
  );
  const isViewingLatestSeason = currentSeasonOption?.isDefault ?? true;
  const activeSeasonOption = seasonOptions.find((opt) => opt.isDefault);

  const handleReturnToActiveSeason = () => {
    if (activeSeasonOption) {
      setSelectedSeasonId(activeSeasonOption.seasonId);
    }
  };

  return (
    <>
      {/* Live game banners — shown when any game is in progress */}
      {liveGames.map((game) => (
        <LiveGameBanner key={game.id} game={game} />
      ))}

      {/* Season alert — shown when viewing a non-latest season */}
      {!isViewingLatestSeason && currentSeasonOption && (
        <Alert variant="info" className="lp-season-alert">
          <div className="lp-season-alert-content">
            <div className="lp-season-alert-text">
              <i className="bi bi-info-circle" style={{ marginRight: "8px" }} />
              <span>
                You are viewing the{" "}
                <strong>{currentSeasonOption.seasonName}</strong> season
              </span>
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleReturnToActiveSeason}
              className="lp-season-alert-button"
            >
              Return to active season
            </Button>
          </div>
        </Alert>
      )}

      {isLoading && (
        <div className="lp-loading">
          <Spinner animation="border" variant="secondary" role="status" />
        </div>
      )}

      {!isLoading && (
        <>
          {/* Standings — full width */}
          <div className="lp-home-section-header">
            <span className="lp-home-section-title">Standings</span>
            <Link to="standings" className="lp-home-section-link">
              Full standings →
            </Link>
          </div>
          <div className="lp-card">
            {standings.length === 0 ? (
              <div className="lp-card-empty">No standings yet.</div>
            ) : (
              <table className="lp-table" aria-label="Standings summary">
                <thead>
                  <tr>
                    <th className="lp-th-left" style={{ width: "18px" }}></th>
                    <th className="lp-th-left">Team</th>
                    <th title="Games played">GP</th>
                    <th className="lp-th-wlt" title="Wins">
                      W
                    </th>
                    <th className="lp-th-wlt" title="Losses">
                      L
                    </th>
                    <th className="lp-th-wlt" title="Ties">
                      T
                    </th>
                    <th className="lp-th-pts" title="Points">
                      PTS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((row, i) => (
                    <tr key={row.team_id}>
                      <td className="lp-td-left lp-rank">{i + 1}</td>
                      <td className="lp-td-left">
                        <Link
                          to={`teams/${row.team_id}`}
                          className="lp-team-link"
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <TeamDisplay
                            team={{
                              id: row.team_id,
                              name: row.team_name,
                              colour_code: row.colour_code,
                            }}
                          />
                        </Link>
                      </td>
                      <td style={{ color: "var(--text-secondary)" }}>
                        {row.games_played}
                      </td>
                      <td className="lp-td-wlt">{row.wins}</td>
                      <td className="lp-td-wlt">{row.losses}</td>
                      <td className="lp-td-wlt">{row.ties}</td>
                      <td className="lp-td-pts lp-pts">{row.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Recent results + upcoming games — two column grid */}
          <div className="lp-home-grid">
            <div className="lp-home-col">
              <div className="lp-home-section-header">
                <span className="lp-home-section-title">Upcoming games</span>
                <Link to="schedule" className="lp-home-section-link">
                  Full schedule →
                </Link>
              </div>
              <div className="lp-card">
                {upcomingGames.length === 0 ? (
                  <div className="lp-card-empty">
                    No upcoming games scheduled.
                  </div>
                ) : (
                  upcomingGames.map((game) => (
                    <GameListItem key={game.id} game={game} />
                  ))
                )}
              </div>
            </div>
            <div className="lp-home-col">
              <div className="lp-home-section-header">
                <span className="lp-home-section-title">Recent results</span>
                <Link to="schedule" className="lp-home-section-link">
                  Full schedule →
                </Link>
              </div>
              <div className="lp-card">
                {recentGames.length === 0 ? (
                  <div className="lp-card-empty">No results yet.</div>
                ) : (
                  recentGames.map((game) => (
                    <GameListItem key={game.id} game={game} />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Announcements placeholder to be rendered in the future */}
          {/* <div className="lp-home-section-header">
            <span className="lp-home-section-title">Announcements</span>
          </div>
          <div className="lp-card">
            <div className="lp-card-empty">No announcements at this time.</div>
          </div> */}
        </>
      )}
    </>
  );
}
